'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Card } from '@/entities/auth'
import { useSignUpGuestStore, SignUpGuestButton } from '@/features/sign-up'
import {
  PASSPORT_VERIFICATION_DIALOG,
  PassportDialog,
  type PhoneVerificationResult,
  type TPassportVerificationForm,
} from '@/features/verify-identity'
import {
  Slot,
  useDialogState,
  WithProvider,
  DialogProvider,
} from '@/shared/lib'
import { PassportVerification, PhoneVerification } from '@/widgets/verification'

import { VerificationStatus as Status } from '../../model/verification'

const Verification = () => {
  const [status, setStatus] = useState<Status>(Status.READY)

  const t = useTranslations()

  const { isOpen, close, closeHandler } = useDialogState()

  const { actions, verification } = useSignUpGuestStore()

  const disabled = status === Status.READY

  const handleSuccessPhoneVerification = ({
    certNum,
    phoneNumber,
  }: PhoneVerificationResult) => {
    actions.updateVerification({ phoneNumber, certNum })
    setStatus(Status.VERIFIED_PHONE)

    toast(t('verification.toast.identity_verification_completed'))
  }

  const handleSuccessPassportVerification = (
    formValues: TPassportVerificationForm,
  ) => {
    close(PASSPORT_VERIFICATION_DIALOG)

    actions.updateVerification(formValues)
    setStatus(Status.VERIFIED_PASSPORT)

    toast(t('passport_verification.toast.passport_photo_uploaded'))
  }

  useEffect(() => {
    actions.updateVerification({
      phoneNumber: '',
      certNum: '',
      contactNumber: '',
      passportImageUrl: '',
    })
  }, [])

  return (
    <Card progress={75}>
      <Slot name="title">{t('verification.title')}</Slot>
      <Slot name="description">{t('verification.description')}</Slot>
      <Slot name="children">
        <div className="space-y-15">
          <PhoneVerification
            disabled={status !== Status.READY}
            isVerified={status === Status.VERIFIED_PHONE}
            onSuccess={handleSuccessPhoneVerification}
            className="mb-10"
          />

          <PassportVerification
            isVerified={status === Status.VERIFIED_PASSPORT}
            disabled={status === Status.VERIFIED_PHONE}
            passportImageUrl={verification?.passportImageUrl}
          />

          {isOpen(PASSPORT_VERIFICATION_DIALOG) && (
            <PassportDialog
              isOpen={isOpen(PASSPORT_VERIFICATION_DIALOG)}
              onClose={closeHandler(PASSPORT_VERIFICATION_DIALOG)}
              onSuccess={handleSuccessPassportVerification}
              defaultValues={{
                contactNumber: verification?.contactNumber,
                passportImageUrl: verification?.passportImageUrl,
              }}
            />
          )}

          <SignUpGuestButton disabled={disabled} />
        </div>
      </Slot>
    </Card>
  )
}

export const SignUpGuestVerification = WithProvider({
  Component: Verification,
  Provider: DialogProvider,
})
